import { isNativeApp } from '../lib/platform';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useInstallPrompt from '../hooks/useInstallPrompt';

const DISMISSED_KEY = 'bb:homescreen-nudge-dismissed-at';
const DISMISS_DAYS = 14;

const isIos = () => {
  if (typeof window === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(window.navigator.userAgent);
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const dismissedRecently = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY));
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const HomescreenInstallNudge = () => {
  const { deferredPrompt, clearInstallPrompt } = useInstallPrompt();
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => dismissedRecently());
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [installMessage, setInstallMessage] = useState('');
  const ios = useMemo(() => isIos(), []);

  useEffect(() => {
    const onInstalled = () => {
      setInstalled(true);
      clearInstallPrompt();
    };

    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [clearInstallPrompt]);

  const handleInstall = useCallback(async () => {
    if (ios) {
      setShowIosSteps(true);
      return;
    }

    if (!deferredPrompt) {
      setInstallMessage(
        'De installatieknop is niet beschikbaar in deze browser. Open het browsermenu en kies “App installeren” of “Toevoegen aan startscherm”.'
      );
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstalled(true);
    }
    clearInstallPrompt();
  }, [clearInstallPrompt, deferredPrompt, ios]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // Ignore localStorage issues; hiding for this session is enough.
    }
    setDismissed(true);
  }, []);

  if (isNativeApp() || installed || dismissed) return null;

  return (
    <section className="mx-auto mt-3 max-w-3xl px-4" aria-label="Cuplio installeren">
      <div className="rounded-2xl bg-gradient-to-r from-purple-600 via-fuchsia-600 to-amber-500 p-[1px] shadow-lg">
        <div className="rounded-2xl bg-white/95 p-4 text-zinc-900 dark:bg-zinc-950/95 dark:text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-purple-700 dark:text-purple-300">
                Sneller naar je rondje
              </p>
              <h2 className="text-lg font-bold leading-tight">Cuplio aanzetten op je homescreen 🍻</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                Voeg Cuplio toe aan je homescreen. Dan voelt Cuplio als een app en ben je klaar voor pushnotificaties.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-full px-2 py-1 text-sm text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
              aria-label="Installatie-tip verbergen"
            >
              ✕
            </button>
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleInstall}
              className="h-11 rounded-xl bg-purple-600 px-4 font-semibold text-white hover:bg-purple-700"
            >
              Toevoegen aan homescreen
            </button>
            <Link
              to="/profile#homescreen-install"
              className="h-11 rounded-xl border border-purple-200 px-4 py-2.5 text-center font-semibold text-purple-700 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-950/40"
            >
              Toevoegen via profiel
            </Link>
          </div>

          {installMessage && (
            <p className="mt-3 text-xs font-medium text-purple-700 dark:text-purple-300" role="status">
              {installMessage}
            </p>
          )}

          {showIosSteps && (
            <div className="mt-3 rounded-xl bg-purple-50 p-3 text-sm text-purple-950 dark:bg-purple-950/40 dark:text-purple-100">
              <ol className="list-decimal space-y-1 pl-4">
                <li>Open Cuplio in Safari.</li>
                <li>Tik op de deelknop onderin.</li>
                <li>Kies <strong>Zet op beginscherm</strong> en daarna <strong>Voeg toe</strong>.</li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomescreenInstallNudge;
